import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
    AppConfigStoreService,
    ConfigStoreResponse,
} from './app-config-store.service';

// Purchase orders live as ONE aggregate document in the App Config Store
// lambda (management-console.reordering) under this key. The reordering
// ROP/EOQ settings live under 'default' in the same collection — never
// write that key from here.
const PURCHASE_ORDERS_KEY = 'purchase-orders';

export interface PurchaseOrderLine {
    // pid is the key (inventory _id / reordering grid keyExpr); sku/title
    // are resolved live from inventory at render time, never stored.
    pid: number;
    qty: number;
}

export interface PurchaseOrder {
    id: string; // client-generated, e.g. PO-260713-153000
    label?: string; // optional supplier name / note
    created_at: string; // ISO datetime
    status: 'open' | 'completed';
    completed_at?: string; // set on complete, removed on reopen
    lines: PurchaseOrderLine[];
}

// Ordering pauses are filed under one of two horizons — a short "skipping
// this round" pause and a long-term one. Both are hidden from ordering until
// restored by hand; the split is what lets them be reviewed and restored
// separately. A pid lives in at most one of the two lists.
export type PauseKind = 'temp' | 'long';

export interface PausedEntry {
    // pid is the key (inventory _id / reordering grid keyExpr); sku/title are
    // resolved live from inventory at render time, never stored.
    pid: number;
    paused_at?: string; // ISO datetime; absent on migrated legacy entries
    reason?: string; // short Thai text for the person ordering (GLP-180)
    source?: string; // report file behind the decision, when a report made it
}

// The whole stored document: the orders plus the items currently paused for
// ordering (hidden from the ordering-mode table until restored), split by
// pause horizon.
export interface PurchaseOrdersDoc {
    orders: PurchaseOrder[];
    paused_temp: PausedEntry[];
    paused_long: PausedEntry[];
}

// Units still on open purchase orders, by pid. The stored document is not
// validated, so an order may have no lines.
export const sumOpenOrderQty = (
    orders: PurchaseOrder[],
): Record<number, number> => {
    const qtyByPid: Record<number, number> = {};
    orders
        .filter((order) => order.status === 'open')
        .forEach((order) =>
            order.lines?.forEach((line) => {
                qtyByPid[line.pid] = (qtyByPid[line.pid] ?? 0) + line.qty;
            }),
        );
    return qtyByPid;
};

// What may actually sit in storage: documents written before the pause split
// carry a single `paused: number[]` instead of the two lists.
interface StoredPurchaseOrdersDoc extends Partial<PurchaseOrdersDoc> {
    paused?: number[];
}

@Injectable({
    providedIn: 'root',
})
export class PurchaseOrderService {
    private configStore = inject(AppConfigStoreService);

    // Returns empty collections until the first save (the lambda answers
    // config: null) or for docs saved before the paused lists existed.
    public getDoc(): Observable<PurchaseOrdersDoc> {
        return this.configStore
            .get<StoredPurchaseOrdersDoc>(PURCHASE_ORDERS_KEY)
            .pipe(
                map((config) => ({
                    orders: config?.orders ?? [],
                    paused_temp: config?.paused_temp ?? [],
                    paused_long:
                        config?.paused_long ?? this.migrateLegacyPaused(config),
                })),
            );
    }

    // The pre-split `paused: number[]` recorded no horizon, so its members
    // land in the long-term list — they have been sitting there since before
    // the split, which is the closer fit, and either way they stay put until
    // restored by hand exactly as before. Only read for documents carrying
    // neither new list, so a stale legacy key left behind by an out-of-band
    // edit cannot resurrect entries that have since been restored.
    private migrateLegacyPaused(
        config: StoredPurchaseOrdersDoc | null,
    ): PausedEntry[] {
        if (
            !config ||
            config.paused_temp ||
            config.paused_long ||
            !Array.isArray(config.paused)
        ) {
            return [];
        }
        return config.paused.map((pid) => ({ pid }));
    }

    // Replaces the whole document — the caller owns the doc and applies
    // create/complete/reopen/pause as transformations before saving. Writing
    // the new shape also drops any legacy `paused` key, since the lambda
    // stores what it is given verbatim (no merge).
    public saveDoc(
        doc: PurchaseOrdersDoc,
    ): Observable<ConfigStoreResponse<PurchaseOrdersDoc>> {
        return this.configStore.set(PURCHASE_ORDERS_KEY, doc);
    }

    // Read-modify-write from a fresh read, for a page that keeps no working
    // copy of the document. Every save replaces the whole document, so
    // building one from an older read would undo whatever was written since.
    // This narrows that race to the gap between the read and the save; closing
    // it would take a compare-and-set in the lambda.
    public updateDoc(
        change: (doc: PurchaseOrdersDoc) => PurchaseOrdersDoc,
    ): Observable<ConfigStoreResponse<PurchaseOrdersDoc>> {
        return this.getDoc().pipe(
            switchMap((doc) => this.saveDoc(change(doc))),
        );
    }
}
